import { CallSplitOutlined } from "@mui/icons-material";
import { type FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemWizardDialog, type WizardStep } from "../../../../../components/UIToolkit/Popups/SystemWizardDialog";
import { useAnalytics } from "../../../../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../../../../contexts/ChosenDepartment";
import { ClipboardConfirmStep } from "./ClipboardConfirmStep";
import { ClipboardCredentialsStep } from "./ClipboardCredentialsStep";
import { ClipboardFacilityStep } from "./ClipboardFacilityStep";
import { type ClipboardRoleMappingSelection, ClipboardRoleMappingStep } from "./ClipboardRoleMappingStep";
import { ClipboardSummaryStep } from "./ClipboardSummaryStep";
import { ClipboardSwitchesAside } from "./ClipboardSwitchesAside";
import {
	useApplyClipboardRoleMapping,
	useClipboardRoleMappingProposal,
	useCreateClipboardEnrollment,
	useVerifyClipboardCredentials,
} from "./clipboardOnboardingHooks";
import { CLIPBOARD_STATUS_MESSAGE_KEY, clipboardErrorMessage, isClipboardUnreachable } from "./clipboardOnboardingLabels";
import type { ClipboardFacilityOption, ClipboardRoleMappingProposalResponse } from "./clipboardOnboardingTypes";

type StepId = "credentials" | "facility" | "confirm" | "roles" | "done";

type Props = {
	open: boolean;
	onClose: () => void;
	/** The department currently has an active `(clipboard, shifts)` row — read off live form state. */
	actualsOn: boolean;
	/**
	 * The enrollment committed. The caller patches `clipboardFacilityId` with the NON-dirtying setter and
	 * refreshes department data: the post-to-agency gates read `chosenDepartment`, not form state, and a
	 * dirty form here would make the next Save re-send a value the server already holds.
	 */
	onEnrolled: (facilityId: string) => void;
	/** Jump to the HRIS Mapping tab, where `SubRoleMappingGrid` stays the manual editor. */
	onOpenHrisMapping: () => void;
};

const emptySelection: ClipboardRoleMappingSelection = { subRoles: [], certOverrides: [], ignores: [] };

/**
 * The support-only guided path onto Clipboard: validate a login, choose the building, commit both in one
 * step, then build the role catalog — the sequence that was previously a hand-run ritual across a secret,
 * a column and two mapping tables, where every missing piece failed silently.
 *
 * The credentials live in component state for the length of the wizard and nowhere else: they are sent
 * to `verify-credentials` (which writes nothing) and again to `enrollment` (which re-mints the token
 * server-side rather than trusting that step 1 passed), and are never logged or tracked.
 */
export const ClipboardSetupWizard: FC<Props> = ({ open, onClose, actualsOn, onEnrolled, onOpenHrisMapping }) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	const { track, trackError } = useAnalytics();

	const [stepId, setStepId] = useState<StepId>("credentials");
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [facilities, setFacilities] = useState<ClipboardFacilityOption[]>([]);
	const [selectedFacilityId, setSelectedFacilityId] = useState<string | null>(null);
	const [enrolledFacility, setEnrolledFacility] = useState<{ facilityId: string; facilityName: string } | null>(null);
	const [roleSelection, setRoleSelection] = useState<ClipboardRoleMappingSelection>(emptySelection);
	const [appliedResult, setAppliedResult] = useState<ClipboardRoleMappingProposalResponse | null>(null);
	const [stepError, setStepError] = useState<string | null>(null);

	const verifyCredentials = useVerifyClipboardCredentials();
	// `onEnrolled` is handed to the HOOK, not to `mutate`: the parent mounts this wizard conditionally, so
	// closing it during the in-flight POST unmounts the observer and TanStack Query v5 then never fires
	// `mutate`-scoped callbacks — the enrollment would commit server-side while `clipboardFacilityId` and
	// `chosenDepartment` stayed stale, and the post-to-agency gates read `chosenDepartment`.
	const createEnrollment = useCreateClipboardEnrollment({ onEnrolled });
	const applyRoleMapping = useApplyClipboardRoleMapping();
	const {
		data: proposal,
		isFetching: isFetchingProposal,
		error: proposalError,
		refetch: refetchProposal,
	} = useClipboardRoleMappingProposal({ facilityId: enrolledFacility?.facilityId ?? null, enabled: stepId === "roles" });

	// The proposal reads Clipboard live, so its most likely failure is Clipboard being down (502) — which
	// wrote nothing and is worth another try, not a dead end. Offer the retry only for that case; a 422
	// (facility not managed) would just fail again.
	const proposalErrorMessage = useMemo(() => {
		if (!proposalError) return null;
		const { key, params } = clipboardErrorMessage(proposalError);
		return t(key, params);
	}, [proposalError, t]);
	const canRetryProposal = !!proposalError && isClipboardUnreachable(proposalError);

	const selectedFacility = facilities.find((facility) => facility.facilityId === selectedFacilityId) ?? null;

	const handleVerify = useCallback(() => {
		setStepError(null);
		track("clipboard_onboarding_verify_start", { department_id: chosenDepartment.department_id });
		verifyCredentials.mutate(
			{ email: email.trim(), password },
			{
				onSuccess: (data) => {
					track("clipboard_onboarding_verify_success", {
						department_id: chosenDepartment.department_id,
						status: data.status,
						facility_count: data.facilities.length,
					});
					if (data.status !== "ok") {
						setStepError(t(CLIPBOARD_STATUS_MESSAGE_KEY[data.status]));
						return;
					}
					setFacilities(data.facilities);
					// A login that manages exactly one free facility has only one possible answer; pre-select it
					// so the common single-building case is a straight Continue rather than a one-option dropdown.
					const free = data.facilities.filter((facility) => !facility.takenByDepartmentId);
					setSelectedFacilityId(free.length === 1 ? free[0].facilityId : null);
					setStepId("facility");
				},
				onError: (error) => {
					trackError("clipboard_onboarding_verify_error", { department_id: chosenDepartment.department_id });
					const { key, params } = clipboardErrorMessage(error);
					setStepError(t(key, params));
				},
			},
		);
	}, [verifyCredentials, email, password, t, track, trackError, chosenDepartment.department_id]);

	const handleEnroll = useCallback(() => {
		if (!selectedFacility) return;
		setStepError(null);
		track("clipboard_onboarding_enroll_start", { department_id: chosenDepartment.department_id, facility_id: selectedFacility.facilityId });
		createEnrollment.mutate(
			{ email: email.trim(), password, facilityId: selectedFacility.facilityId },
			{
				// Advance + analytics only. The form/department sync lives at hook level so it cannot be lost.
				onSuccess: (data) => {
					track("clipboard_onboarding_enroll_success", { department_id: chosenDepartment.department_id, facility_id: data.facilityId });
					setEnrolledFacility({ facilityId: data.facilityId, facilityName: data.facilityName });
					setStepId("roles");
				},
				onError: (error) => {
					trackError("clipboard_onboarding_enroll_error", { department_id: chosenDepartment.department_id });
					const { key, params } = clipboardErrorMessage(error);
					setStepError(t(key, params));
				},
			},
		);
	}, [createEnrollment, selectedFacility, email, password, t, track, trackError, chosenDepartment.department_id]);

	const selectedRoleCount = roleSelection.subRoles.length + roleSelection.certOverrides.length + roleSelection.ignores.length;

	const handleApplyRoles = useCallback(() => {
		if (!enrolledFacility || selectedRoleCount === 0) {
			setStepId("done");
			return;
		}
		setStepError(null);
		track("clipboard_onboarding_role_mapping_start", { department_id: chosenDepartment.department_id, row_count: selectedRoleCount });
		applyRoleMapping.mutate(
			// `roleSelection` arrives already in payload shape from the mapping step, which is the only place
			// that can prove a row's target job + sub-role are present — so there is no fallback to apply here.
			{ facilityId: enrolledFacility.facilityId, ...roleSelection },
			{
				onSuccess: (data) => {
					track("clipboard_onboarding_role_mapping_success", {
						department_id: chosenDepartment.department_id,
						written: selectedRoleCount,
						unmapped_remaining: data.rows.filter((row) => row.disposition === "unmapped").length,
					});
					setAppliedResult(data);
					setStepId("done");
				},
				onError: (error) => {
					trackError("clipboard_onboarding_role_mapping_error", { department_id: chosenDepartment.department_id });
					const { key, params } = clipboardErrorMessage(error);
					setStepError(t(key, params));
				},
			},
		);
	}, [applyRoleMapping, enrolledFacility, roleSelection, selectedRoleCount, t, track, trackError, chosenDepartment.department_id]);

	const aside = (activeSwitch: "enrollment" | "facility" | undefined) => (
		<ClipboardSwitchesAside enrolled={!!enrolledFacility} facilityMapped={!!enrolledFacility} actualsOn={actualsOn} activeSwitch={activeSwitch} />
	);

	// After the apply, the residual count is the server's own re-classification. Before it (mapping
	// skipped), fall back to the proposal — same classifier, just not re-run. `null` when neither ever
	// loaded, so the summary says "unknown" instead of inventing a clean bill of health.
	const residualRows = appliedResult?.rows ?? proposal?.rows ?? null;
	const unmappedCount = residualRows === null ? null : residualRows.filter((row) => row.disposition === "unmapped").length;

	const steps: WizardStep<StepId>[] = [
		{
			id: "credentials",
			label: t("department.config.clipboard.wizard.step.credentials"),
			content: (
				<ClipboardCredentialsStep
					email={email}
					onEmailChange={setEmail}
					password={password}
					onPasswordChange={setPassword}
					errorMessage={stepError}
					onSubmit={handleVerify}
					aside={aside("enrollment")}
				/>
			),
			primaryLabel: t("department.config.clipboard.wizard.credentials.action"),
			onPrimary: handleVerify,
			primaryDisabled: !email.trim() || !password || verifyCredentials.isPending,
			primaryLoading: verifyCredentials.isPending,
		},
		{
			id: "facility",
			label: t("department.config.clipboard.wizard.step.facility"),
			content: (
				<ClipboardFacilityStep
					facilities={facilities}
					selectedFacilityId={selectedFacilityId}
					onSelect={setSelectedFacilityId}
					departmentTimezone={chosenDepartment.timezone}
					aside={aside("facility")}
				/>
			),
			primaryLabel: t("continue"),
			onPrimary: () => {
				setStepError(null);
				setStepId("confirm");
			},
			primaryDisabled: !selectedFacility,
			primaryTooltip: selectedFacility ? undefined : t("department.config.clipboard.wizard.facility.pickFirst"),
		},
		{
			id: "confirm",
			label: t("department.config.clipboard.wizard.step.confirm"),
			content: selectedFacility ? (
				<ClipboardConfirmStep departmentName={chosenDepartment.display_name} facility={selectedFacility} errorMessage={stepError} aside={aside("facility")} />
			) : null,
			primaryLabel: t("department.config.clipboard.wizard.confirm.action"),
			onPrimary: handleEnroll,
			primaryDisabled: !selectedFacility || createEnrollment.isPending,
			primaryLoading: createEnrollment.isPending,
		},
		{
			id: "roles",
			label: t("department.config.clipboard.wizard.step.roles"),
			content: (
				<ClipboardRoleMappingStep
					proposal={proposal}
					isLoading={isFetchingProposal}
					errorMessage={stepError ?? proposalErrorMessage}
					onRetry={canRetryProposal ? () => void refetchProposal() : undefined}
					onSelectionChange={setRoleSelection}
				/>
			),
			primaryLabel: selectedRoleCount > 0 ? t("department.config.clipboard.wizard.roles.action.apply") : t("continue"),
			onPrimary: handleApplyRoles,
			primaryDisabled: isFetchingProposal || applyRoleMapping.isPending,
			primaryLoading: applyRoleMapping.isPending,
			// The enrollment is committed by this point; "Back" would only invite re-running a write.
			hideBack: true,
		},
		{
			id: "done",
			label: t("department.config.clipboard.wizard.step.done"),
			content: enrolledFacility ? (
				<ClipboardSummaryStep
					facilityName={enrolledFacility.facilityName}
					facilityId={enrolledFacility.facilityId}
					writtenCount={appliedResult ? selectedRoleCount : 0}
					unmappedCount={unmappedCount}
					actualsOn={actualsOn}
					onOpenHrisMapping={() => {
						onOpenHrisMapping();
						onClose();
					}}
				/>
			) : null,
			primaryLabel: t("done"),
			onPrimary: onClose,
			hideBack: true,
			hideClose: true,
		},
	];

	// Belt to the hook's braces: while the enrollment is committing there is no safe moment to walk away,
	// so the close button, the backdrop and Esc are all inert until it settles.
	const handleClose = useCallback(() => {
		if (createEnrollment.isPending) return;
		onClose();
	}, [createEnrollment.isPending, onClose]);

	const subtitle = useMemo(
		() => t("department.config.clipboard.wizard.subtitle", { department: chosenDepartment.display_name }),
		[t, chosenDepartment.display_name],
	);

	return (
		<SystemWizardDialog<StepId>
			open={open}
			onClose={handleClose}
			icon={<CallSplitOutlined />}
			title={t("department.config.clipboard.wizard.title")}
			subtitle={subtitle}
			steps={steps}
			activeStepId={stepId}
			onStepChange={(next) => {
				setStepError(null);
				setStepId(next);
			}}
			backLabel={t("back")}
			closeLabel={t("close")}
			railAriaLabel={t("department.config.clipboard.wizard.rail.ariaLabel")}
			dataTestId="clipboard-setup-wizard"
		/>
	);
};
